import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, ShieldOff, MoreHorizontal, ArrowLeft, UserRound, Ban, Flag, MessageSquareWarning } from "lucide-react";
import { MAX_REPLY_LENGTH, SAM } from "./phaseD";
import { avatarUrl } from "./wildrushPlates";

const ACCENT = "#4FA3D9";

// Fictional stylised WILD RUSH player portraits (shared set with multiplayer tags and the gallery). Non-photorealistic, age-neutral.
export const Avatar = ({ name = SAM, size = 40, testid }) => (
  <img src={avatarUrl(name)} width={size} height={size} alt="" aria-hidden="true" draggable={false} data-testid={testid || (name === SAM ? "sam17-avatar" : `avatar-${name}`)}
    className="shrink-0 rounded-full border border-white/20 bg-[#0f2a33] object-cover shadow-[0_2px_6px_rgba(0,0,0,.35)]" style={{ width: size, height: size }} />
);
export const Sam17Avatar = ({ size = 40 }) => <Avatar name={SAM} size={size} />;

export const Bubble = ({ who, text, name = SAM, latest }) => {
  const you = who === "you";
  const sys = who === "system";
  if (sys) return <div className="self-center rounded-full bg-white/12 px-3.5 py-1.5 text-[12px] font-medium text-white/80" data-testid="chat-system">{text}</div>;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex max-w-[88%] items-end gap-2 ${you ? "self-end flex-row-reverse" : "self-start"} ${latest ? "" : "opacity-[.82]"}`} data-testid={you ? "chat-you" : "chat-sam"}>
      {!you && <Avatar name={name} size={28} />}
      <div className={`rounded-2xl px-4 py-2.5 text-[16px] leading-[1.45] shadow-[0_2px_10px_rgba(0,0,0,.18)] ${you ? "rounded-br-md bg-[#4FA3D9] text-[#06131a]" : "rounded-bl-md border border-white/10 bg-[#07171d]/85 text-white"}`}>
        {!you && <span className="mb-0.5 block text-[13px] font-semibold tracking-wide text-[#9fe0c0]">{name}</span>}{text}
      </div>
    </motion.div>
  );
};

export const Thread = ({ items, typing }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [items.length, typing]);
  return (
    <div className="flex max-h-[36dvh] flex-col gap-2.5 overflow-y-auto pr-1" role="log" aria-live="polite" aria-relevant="additions" data-testid="chat-thread">
      {items.map((m, i) => <Bubble key={i} who={m.who} text={m.text} name={m.name} latest={i === items.length - 1} />)}
      {typing && <div className="self-start pl-9 text-[13px] font-medium text-white/75" data-testid="chat-typing">{SAM} is typing…</div>}
      <div ref={end} />
    </div>
  );
};

export const Composer = ({ onSend, disabled, autoFocus }) => {
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); const t = text.trim(); if (!t || disabled) return; setText(""); onSend(t); };
  return (
    <form onSubmit={submit} className="flex items-center gap-2" data-testid="chat-composer">
      <input value={text} onChange={(e) => setText(e.target.value.slice(0, MAX_REPLY_LENGTH))} maxLength={MAX_REPLY_LENGTH} autoFocus={autoFocus}
        placeholder="Type a reply…" aria-label="Type a reply" disabled={disabled} autoComplete="off" autoCorrect="on" enterKeyHint="send"
        className="min-h-[48px] flex-1 rounded-full border border-white/20 bg-[#06131a]/70 px-4 text-[16px] text-white placeholder:text-white/50 outline-none transition-colors focus:border-[#4FA3D9] focus-visible:ring-2 focus-visible:ring-[#4FA3D9]/60 disabled:opacity-60" data-testid="chat-input" />
      <button type="submit" disabled={disabled || !text.trim()} aria-label="Send reply" data-testid="chat-send"
        className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#4FA3D9] text-[#06131a] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-40"><Send size={18} /></button>
    </form>
  );
};

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4FA3D9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#06131a]";
// selected = chosen (never "correct"): accent border + modest accent fill.
export const Chip = ({ id, label, onClick, disabled, icon: Icon, quiet, selected }) => (
  <button onClick={onClick} disabled={disabled} data-testid={`wr-action-${id}`} aria-pressed={selected === undefined ? undefined : selected}
    className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-full border px-4 text-[15px] font-semibold backdrop-blur transition-colors ${FOCUS} ${
      selected ? "border-[#4FA3D9] bg-[#4FA3D9]/30 text-white shadow-[0_0_0_1px_rgba(79,163,217,.45)]"
      : quiet ? "border-white/15 bg-[#06131a]/45 text-white/85 hover:bg-white/10 disabled:opacity-50" : "border-white/25 bg-white/12 text-white hover:bg-white/20 disabled:opacity-50"}`}>
    {Icon && <Icon size={16} style={{ color: quiet && !selected ? undefined : ACCENT }} className={quiet && !selected ? "text-white/65" : ""} />}{label}
  </button>
);

const TOOL_META = { back: { icon: ArrowLeft }, profile: { icon: UserRound }, block: { icon: ShieldOff }, more: { icon: MoreHorizontal } };
export const Tools = ({ tools, labels, onTool, disabled }) => (
  <div className="flex flex-wrap gap-2" data-testid="wr-tools">
    {tools.map((t) => <Chip key={t} id={t} label={labels[t]} icon={TOOL_META[t].icon} quiet onClick={() => onTool(t)} disabled={disabled} />)}
  </div>
);

export const RequestCard = ({ kind, actions, onAction, disabled }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/15 bg-[#0d1418]/90 p-4 backdrop-blur" role="dialog" aria-label={kind === "friend" ? "Friend request" : "Private chat request"} data-testid={kind === "friend" ? "friend-request-card" : "private-chat-request-card"}>
    <p className="text-[12px] font-semibold tracking-[0.18em] text-white/60">{kind === "friend" ? "🔔 FRIEND REQUEST" : "PRIVATE CHAT REQUEST"}</p>
    <div className="mt-3 flex items-center gap-3">
      <Sam17Avatar size={40} />
      <div><p className="text-[16px] font-semibold">{SAM}</p><p className="text-[15px] text-white/70">{kind === "friend" ? "wants to add you as a friend" : "wants to chat privately"}</p></div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {actions.map((a) => <Chip key={a.id} id={a.id} label={a.label} onClick={() => onAction(a.id)} disabled={disabled} quiet={a.id === "profile"} />)}
    </div>
  </motion.div>
);

export const BlockConfirm = ({ title = `Block ${SAM}?`, body = "They won't be able to message you from this account.", onConfirm, onCancel }) => (
  <Overlay label={title} testid="block-confirm" onDismiss={onCancel}>
    <p className="text-lg font-semibold">{title}</p>
    <p className="mt-2 text-sm text-white/65">{body}</p>
    <div className="mt-5 flex gap-2">
      <Chip id="block-confirm" label="Block" icon={Ban} onClick={onConfirm} />
      <Chip id="block-cancel" label="Cancel" quiet onClick={onCancel} />
    </div>
  </Overlay>
);

export const MoreMenu = ({ showTell, onPick, onClose }) => (
  <Overlay label="More options" testid="more-menu" onDismiss={onClose}>
    <div className="flex flex-col gap-2">
      <Chip id="report" label="Report" icon={Flag} quiet onClick={() => onPick("report")} />
      {showTell && <Chip id="tell" label="Tell someone" icon={MessageSquareWarning} quiet onClick={() => onPick("tell")} />}
      <Chip id="more-close" label="Close" quiet onClick={onClose} />
    </div>
  </Overlay>
);

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Shared dialog behaviour for dismissible Sim #2 overlays: initial focus, Tab/Shift+Tab containment, Escape → onDismiss (when provided),
// focus restored to the invoking control on close. Non-dismissible overlays simply omit onDismiss.
export const Overlay = ({ children, label, testid, onDismiss }) => {
  const panel = useRef(null);
  const dismiss = useRef(onDismiss); dismiss.current = onDismiss;
  useEffect(() => {
    const invoker = document.activeElement;
    const first = panel.current?.querySelector(FOCUSABLE);
    (first || panel.current)?.focus({ preventScroll: true });
    return () => { if (invoker && document.contains(invoker)) invoker.focus({ preventScroll: true }); };
  }, []);
  const onKeyDown = (e) => {
    if (e.key === "Escape" && dismiss.current) { e.stopPropagation(); e.preventDefault(); dismiss.current(); return; }
    if (e.key !== "Tab" || !panel.current) return;
    const items = Array.from(panel.current.querySelectorAll(FOCUSABLE));
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-40 flex items-end justify-center bg-black/55 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={label} data-testid={testid} onKeyDown={onKeyDown}>
        <motion.div ref={panel} tabIndex={-1} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-sm rounded-3xl border border-white/15 bg-[#0d1418] p-5 text-white shadow-2xl outline-none">{children}</motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
